import React, { useState, useEffect } from 'react';
import { FaArrowRight, FaArrowLeft, FaCloud, FaServer, FaShieldAlt, FaLaptopCode } from 'react-icons/fa';

const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: 'Complete IT Solutions for Your',
      highlight: 'Business Growth',
      description: 'We provide comprehensive information technology services including cloud computing, software solutions, volume licensing, and IT consulting to help your business thrive in the digital age.',
      image: '/images/banners/ban1.jpg',
      buttonText: 'Our Services',
      buttonLink: '#services',
    },
    {
      title: 'Electric Cloud',
      highlight: 'Solutions',
      description: 'As an authorized Microsoft Partner, we deliver genuine Microsoft products, Azure cloud solutions, Microsoft 365, and enterprise licensing at competitive prices.',
      image: '/images/banners/connector.jpg',
      buttonText: 'View Products',
      buttonLink: '#products',
    },
    {
      title: 'Web Implementation &',
      highlight: 'Development',
      description: 'Transform your business with our cloud migration, infrastructure setup, and managed IT services. We ensure seamless digital transformation for enterprises of all sizes.',
      image: '/images/banners/ban2.jpg',
      buttonText: 'Learn More',
      buttonLink: '#about',
    },
    {
      title: 'Networking &',
      highlight: 'Infrastructure',
      description: 'Build a robust IT infrastructure with our comprehensive networking solutions. From setup to maintenance, we handle all your networking needs.',
      image: '/images/banners/ban4.png',
      buttonText: 'Get Started',
      buttonLink: '#services',
    },
    {
      title: 'Digital Transformation for',
      highlight: 'Modern Business',
      description: 'Protect your organization from cyber threats with our comprehensive security solutions including threat assessment, security implementation, and 24/7 monitoring.',
      image: '/images/banners/ban3.jpg',
      buttonText: 'Get Protected',
      buttonLink: '#contact',
    },
    {
      title: 'Enterprise IT',
      highlight: 'Solutions',
      description: 'End-to-end IT solutions tailored for your enterprise needs. From consulting to implementation, we are your technology partner.',
      image: '/images/banners/2.jpg',
      buttonText: 'Contact Us',
      buttonLink: '#contact',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const features = [
    { icon: <FaCloud className="text-3xl" />, title: 'Cloud Solutions', desc: 'Azure, AWS & Hybrid' },
    { icon: <FaServer className="text-3xl" />, title: 'IT Infrastructure', desc: 'Setup & Management' },
    { icon: <FaShieldAlt className="text-3xl" />, title: 'Cyber Security', desc: '24/7 Protection' },
    { icon: <FaLaptopCode className="text-3xl" />, title: 'Software Solutions', desc: 'Custom Development' },
  ];

  return (
    <section id="home" className="relative">
      {/* Main Carousel */}
      <div className="relative h-screen min-h-[600px] max-h-[800px] overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
          >
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/80 to-blue-900/70"></div>
            </div>

            {/* Content */}
            <div className="relative h-full container mx-auto px-4 flex items-center">
              <div className="max-w-3xl text-white">
                <div className="inline-block bg-orange-500 px-4 py-2 rounded-full mb-6 animate-pulse">
                  <span className="text-sm font-semibold">Microsoft Silver Certified Partner</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  {slide.title}
                  <span className="text-orange-400 block">{slide.highlight}</span>
                </h1>

                <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed max-w-2xl">
                  {slide.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={slide.buttonLink}
                    className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105"
                  >
                    {slide.buttonText}
                    <FaArrowRight className="ml-2" />
                  </a>
                  <a
                    href="#contact"
                    className="inline-flex items-center justify-center border-2 border-white hover:bg-white hover:text-blue-900 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white p-4 rounded-full transition-all duration-300"
        >
          <FaArrowLeft size={20} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white p-4 rounded-full transition-all duration-300"
        >
          <FaArrowRight size={20} />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-orange-500 w-8' : 'bg-white/50 hover:bg-white'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Features Bar */}
      <div className="bg-white shadow-xl relative z-20 -mt-16 mx-4 md:mx-auto max-w-6xl rounded-2xl">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-200">
          {features.map((feature, index) => (
            <div key={index} className="p-6 text-center hover:bg-blue-50 transition-colors duration-300 first:rounded-l-2xl last:rounded-r-2xl">
              <div className="text-blue-600 flex justify-center mb-3">{feature.icon}</div>
              <h3 className="font-bold text-gray-800 mb-1">{feature.title}</h3>
              <p className="text-sm text-gray-500">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-12 mt-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <h3 className="text-4xl md:text-5xl font-bold mb-2">500+</h3>
              <p className="text-blue-200">Happy Clients</p>
            </div>
            <div>
              <h3 className="text-4xl md:text-5xl font-bold mb-2">7+</h3>
              <p className="text-blue-200">Years Experience</p>
            </div>
            <div>
              <h3 className="text-4xl md:text-5xl font-bold mb-2">100+</h3>
              <p className="text-blue-200">Projects Completed</p>
            </div>
            <div>
              <h3 className="text-4xl md:text-5xl font-bold mb-2">24/7</h3>
              <p className="text-blue-200">Support Available</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
